import React, { useState, useRef, useContext, useEffect } from 'react';
import DOMPurify from 'dompurify';
import BookmarkAddedIcon from '@mui/icons-material/BookmarkAdded';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import { formatDate } from '../functions/DateFormatters';
import { useApiDataContext } from './ApiDataContext';
import { useAuth } from './AuthContext';
import supabase from './supabaseClient';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import { ModalContext } from "../App";
import CloseIcon from '@mui/icons-material/Close';
import RestoreIcon from '@mui/icons-material/Restore';
import IosShareIcon from '@mui/icons-material/IosShare';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

// Sanitize and clean text for preview
const sanitizePreviewText = (text) => {
    if (!text) return '';

    const temp = document.createElement('div');
    temp.innerHTML = text;

    let cleanText = temp.textContent || temp.innerText;

    cleanText = cleanText
        .replace(/\s+/g, ' ')
        .replace(/[^\w\s.,!?-]/g, '')
        .trim();

    return cleanText;
};

// Title case helper
const SHORT_ACRONYMS = new Set(['us', 'uk', 'eu', 'apac', 'emea', 'latam', 'mena']);

const toTitleCase = (str) => {
    if (!str) return '';
    return str
        .toLowerCase()
        .split(' ')
        .map(word => SHORT_ACRONYMS.has(word) ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

// Get source info from type/url
const getSourceInfo = (type, url) => {
    const lowerType = (type || '').toLowerCase();
    const lowerUrl = (url || '').toLowerCase();

    if (lowerUrl.includes('reddit.com') || lowerType.includes('reddit')) {
        return { label: 'Reddit', color: '#FF4500' };
    }
    if (lowerUrl.includes('twitter.com') || lowerUrl.includes('x.com') || lowerType === 'twitter') {
        return { label: 'Twitter', color: '#1DA1F2' };
    }
    if (lowerUrl.includes('yelp.com') || lowerType === 'yelp') {
        return { label: 'Yelp', color: '#D32323' };
    }
    if (lowerUrl.includes('google') || lowerType === 'google') {
        return { label: 'Google', color: '#4285F4' };
    }
    if (lowerType.includes('app_store') || lowerUrl.includes('apple.com')) {
        return { label: 'App Store', color: '#007AFF' };
    }
    if (lowerType.includes('play') || lowerUrl.includes('play.google')) {
        return { label: 'Play Store', color: '#34A853' };
    }
    return { label: type || 'Source', color: '#6B7280' };
};

// Confidence color
const getConfidenceColor = (confidence) => {
    if (confidence >= 8) return { bg: '#DCFCE7', text: '#166534' };
    if (confidence >= 6) return { bg: '#FEF9C3', text: '#854D0E' };
    return { bg: '#FEE2E2', text: '#991B1B' };
};

// Format date as "Mon DD, YYYY"
const formatPostDate = (timeString) => {
    const date = new Date(timeString);
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
};

// valueColors: optional (rawValue) => ({ bg, text } | null) to override the
// default blue value-tag chip (e.g. star ratings scaled red→green)
function MotivationPost({ post, query, motivationType, valueColors }) {
    const { fetchBookmarks, bookmarkFolders, handleBookmark, orderedFolders, handleStar } = useApiDataContext();
    const { userId } = useAuth();

    // State
    const [isHovered, setIsHovered] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
    const [bookmarked, setBookmarked] = useState(false);
    const [bookmarkFolder, setBookmarkFolder] = useState(null);
    const [isDeleted, setIsDeleted] = useState(false);
    const [foldersInOrder, setFoldersInOrder] = useState([]);
    const [modifiedText, setModifiedText] = useState('');
    const [showExplanation, setShowExplanation] = useState(false);
    const [isStarred, setIsStarred] = useState(post.is_starred || false);
    const [expanded, setExpanded] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(false);

    // Refs
    const buttonRef = useRef(null);
    const menuRef = useRef(null);
    const closeTimeoutRef = useRef(null);

    // Context
    const modalContext = useContext(ModalContext);
    const openNewFolderModal = modalContext?.openNewFolderModal;

    // Derived values
    const values = Array.isArray(post.value) ? post.value : [post.value];
    const formattedValues = values.map(v => toTitleCase(v));
    const tagColorFor = (idx) => (valueColors && valueColors(values[idx])) || { bg: '#DBEAFE', text: '#1E40AF' };
    const sourceInfo = getSourceInfo(post.type, post.url);
    const confidenceScore = 'confidence' in post ? post.confidence : (post.rating != null ? post.rating : null);
    const confidenceColors = getConfidenceColor(confidenceScore);

    // Check bookmark status
    useEffect(() => {
        if (bookmarkFolders && post.id in bookmarkFolders) {
            setBookmarked(true);
            setBookmarkFolder(bookmarkFolders[post.id]);
        } else {
            setBookmarked(false);
            setBookmarkFolder(null);
        }
    }, [bookmarkFolders, post.id]);

    // Order folders with bookmarked folder first
    useEffect(() => {
        if (orderedFolders && orderedFolders.length > 0) {
            if (bookmarkFolder) {
                const foldersIn = [...orderedFolders];
                const bookmarkedFolderObj = foldersIn.find(folder => folder.folder_id === bookmarkFolder);
                if (bookmarkedFolderObj) {
                    foldersIn.splice(foldersIn.indexOf(bookmarkedFolderObj), 1);
                    foldersIn.unshift(bookmarkedFolderObj);
                }
                setFoldersInOrder(foldersIn);
            } else {
                setFoldersInOrder(orderedFolders);
            }
        }
    }, [orderedFolders, bookmarkFolder]);

    // Process text with URL detection and highlighting
    useEffect(() => {
        if (post.text) {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = post.text;

            // Process URLs
            const processNode = (node) => {
                if (node.nodeType === Node.TEXT_NODE) {
                    const urlRegex = /\b(?:https?:\/\/\S+)/gi;
                    const processedText = node.textContent.replace(urlRegex, url => {
                        return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
                    });

                    if (processedText !== node.textContent) {
                        const span = document.createElement('span');
                        span.innerHTML = processedText;
                        node.parentNode.replaceChild(span, node);
                    }
                } else if (node.nodeType === Node.ELEMENT_NODE && node.tagName !== 'A') {
                    Array.from(node.childNodes).forEach(processNode);
                }
            };

            Array.from(tempDiv.childNodes).forEach(processNode);

            // Apply highlighting if query exists
            if (query && query.length > 2) {
                const queryTerms = query.split(' ').map(term => term.toLowerCase());
                const queryRoots = queryTerms.map(term => term.toLowerCase().replace(/(ing|ed|s|er|ation|ers|es)$/i, ''));

                const highlightNode = (node) => {
                    if (node.nodeType === Node.TEXT_NODE) {
                        const sentences = node.textContent.split(/([.!?]+\s+|\n+)/g);
                        let highlightedText = '';
                        let shouldHighlight = false;

                        sentences.forEach(sentence => {
                            const sentenceLower = sentence.toLowerCase();
                            const containsQueryTerm = queryRoots.some(term =>
                                sentenceLower.match(new RegExp(`\\b${term}(?:ing|ed|s|er|ation|ers|es)?\\b`, 'i'))
                            );

                            if (containsQueryTerm) {
                                highlightedText += `<span class="highlight">${sentence}</span>`;
                                shouldHighlight = true;
                            } else {
                                highlightedText += sentence;
                            }
                        });

                        if (shouldHighlight) {
                            const span = document.createElement('span');
                            span.innerHTML = highlightedText;
                            node.parentNode.replaceChild(span, node);
                        }
                    } else if (node.nodeType === Node.ELEMENT_NODE && node.tagName !== 'A') {
                        Array.from(node.childNodes).forEach(highlightNode);
                    }
                };

                Array.from(tempDiv.childNodes).forEach(highlightNode);
            }

            setModifiedText(tempDiv.innerHTML);
        }
    }, [query, post.text]);

    // Close menu on scroll
    useEffect(() => {
        const handleScroll = () => {
            if (isHovered) setIsHovered(false);
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [isHovered]);

    // Cleanup timeout
    useEffect(() => {
        return () => {
            if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
        };
    }, []);

    // Handlers
    const handleMouseEnter = () => {
        if (!buttonRef.current) return;

        const rect = buttonRef.current.getBoundingClientRect();
        setMenuPosition({
            x: window.innerWidth - rect.right - 4,
            y: rect.bottom + 2
        });

        if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
        setIsHovered(true);
    };

    const handleMouseLeave = () => {
        closeTimeoutRef.current = setTimeout(() => setIsHovered(false), 50);
    };

    const handleMenuMouseEnter = () => {
        if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };

    const handleMenuMouseLeave = () => {
        closeTimeoutRef.current = setTimeout(() => setIsHovered(false), 50);
    };

    const handleBookmarkClick = async (folder) => {
        if (bookmarked && folder === bookmarkFolder) {
            setBookmarked(false);
        } else if (bookmarked && folder === null) {
            setBookmarked(false);
        } else if (bookmarked && folder !== bookmarkFolder) {
            setBookmarkFolder(folder);
        } else {
            setBookmarked(true);
            setBookmarkFolder(folder);
        }
        handleBookmark(post, folder, bookmarked, bookmarkFolder);
    };

    const handleDelete = async () => {
        setIsDeleted(true);
        await supabase
            .from('deleted_posts')
            .upsert({ user_id: userId, post_id: post.id })
            .select();
        await supabase
            .from('bookmarks')
            .delete()
            .match({ user_id: userId, id: post.id })
            .select();
        fetchBookmarks();
    };

    const undoDelete = async () => {
        setIsDeleted(false);
        await supabase
            .from('deleted_posts')
            .delete()
            .match({ user_id: userId, post_id: post.id })
            .select();
    };

    const handleNewFolder = async () => {
        if (openNewFolderModal) openNewFolderModal(post.id);
    };

    const handleStarClick = async () => {
        setIsStarred(!isStarred);
        handleStar(post.id, isStarred);
    };

    const toggleExpand = () => setExpanded(!expanded);

    const handleCollapseClick = () => setIsCollapsed(!isCollapsed);

    const handleDeleteRating = async () => {
        try {
            const { error } = await supabase
                .from('post_assessments')
                .delete()
                .match({
                    id: post.id,
                    type: post.ratingType || motivationType,
                    topic_id: post.topic_id
                });

            if (error) throw error;
            setShowExplanation(false);
        } catch (error) {
            console.error('Error deleting rating:', error);
        }
    };

    // Render text with truncation
    const renderText = () => {
        if (!modifiedText) return null;

        const charLimit = 800;
        const displayAmount = 400;

        if (modifiedText.length <= charLimit || expanded) {
            return (
                <div
                    className="post-text"
                    style={{
                        fontSize: '14px',
                        lineHeight: 1.6,
                        color: '#1F2937',
                        wordBreak: 'break-word'
                    }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(modifiedText) }}
                    onClick={(e) => {
                        if (e.target.tagName === 'A') {
                            e.preventDefault();
                            window.open(e.target.href, '_blank');
                        }
                    }}
                />
            );
        }

        let truncatedText = modifiedText.substring(0, displayAmount);
        const lastSpaceIndex = truncatedText.lastIndexOf(' ');
        if (lastSpaceIndex > 0) truncatedText = truncatedText.substring(0, lastSpaceIndex);
        truncatedText = truncatedText.trim() + '...';

        return (
            <>
                <div
                    className="post-text"
                    style={{
                        fontSize: '14px',
                        lineHeight: 1.6,
                        color: '#1F2937',
                        wordBreak: 'break-word'
                    }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(truncatedText) }}
                />
                <button
                    onClick={toggleExpand}
                    style={{
                        background: 'none',
                        border: 'none',
                        color: '#2563EB',
                        cursor: 'pointer',
                        fontSize: '13px',
                        padding: '4px 0',
                        fontWeight: 500
                    }}
                >
                    Read more
                </button>
            </>
        );
    };

    // Deleted state
    if (isDeleted) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                backgroundColor: '#FEF2F2',
                borderRadius: '8px',
                marginBottom: '12px'
            }}>
                <span style={{ color: '#991B1B', fontSize: '13px' }}>
                    Post deleted. We'll use this feedback to improve.
                </span>
                <button
                    onClick={undoDelete}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        background: 'none',
                        border: '1px solid #991B1B',
                        color: '#991B1B',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                    }}
                >
                    <RestoreIcon style={{ fontSize: 16 }} />
                    Undo
                </button>
            </div>
        );
    }

    // Collapsed state
    if (isCollapsed) {
        return (
            <div
                onClick={handleCollapseClick}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '10px 16px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: '1px solid #E5E7EB',
                    marginBottom: '12px',
                    cursor: 'pointer'
                }}
            >
                <ExpandMoreIcon style={{ color: '#9CA3AF', fontSize: 20 }} />
                <span style={{ fontSize: '12px', color: '#6B7280' }}>
                    {formatPostDate(post.time)}
                </span>
                <span style={{ fontSize: '13px', color: '#374151', flex: 1 }}>
                    {sanitizePreviewText(post.text)?.substring(0, 100) || 'No content'}...
                </span>
                {formattedValues.slice(0, 2).map((val, idx) => (
                    <span key={idx} style={{
                        padding: '2px 8px',
                        fontSize: '10px',
                        fontWeight: 600,
                        color: tagColorFor(idx).text,
                        backgroundColor: tagColorFor(idx).bg,
                        borderRadius: '12px'
                    }}>
                        {val}
                    </span>
                ))}
            </div>
        );
    }

    // Full post view
    return (
        <div style={{
            backgroundColor: 'white',
            borderRadius: '10px',
            border: '1px solid #E5E7EB',
            padding: '16px',
            marginBottom: '12px',
            position: 'relative'
        }}>
            {/* Collapse handle */}
            <div
                onClick={handleCollapseClick}
                style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: '24px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    paddingTop: '16px',
                    cursor: 'pointer',
                    color: '#D1D5DB'
                }}
            >
                <ExpandLessIcon style={{ fontSize: 18 }} />
            </div>

            <div style={{ marginLeft: '16px' }}>
                {/* First row: Value tags */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '8px',
                    flexWrap: 'wrap'
                }}>
                    {formattedValues.map((val, idx) => (
                        <span key={idx} style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '4px 10px',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: tagColorFor(idx).text,
                            backgroundColor: tagColorFor(idx).bg,
                            borderRadius: '16px'
                        }}>
                            {val}
                        </span>
                    ))}
                </div>

                {/* Second row: Confidence + Source + Time + Actions */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '12px',
                    fontSize: '11px',
                    color: '#6B7280'
                }}>
                    {/* Confidence badge - clickable */}
                    {confidenceScore != null && confidenceScore !== '' && (
                    <span
                        onClick={() => post.explanation && setShowExplanation(!showExplanation)}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '4px 8px',
                            fontSize: '11px',
                            fontWeight: 500,
                            color: confidenceColors.text,
                            backgroundColor: confidenceColors.bg,
                            borderRadius: '12px',
                            cursor: post.explanation ? 'pointer' : 'default'
                        }}
                    >
                        Confidence: {confidenceScore}/10
                    </span>
                    )}

                    <span style={{
                        padding: '2px 6px',
                        backgroundColor: '#F3F4F6',
                        borderRadius: '4px',
                        fontWeight: 500
                    }}>
                        {sourceInfo.label}
                    </span>

                    <span>{formatPostDate(post.time)}</span>

                    {/* Spacer */}
                    <div style={{ flex: 1 }} />

                    {/* Action buttons */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <button
                            onClick={handleDelete}
                            style={{
                                background: 'none',
                                border: 'none',
                                padding: '4px',
                                cursor: 'pointer',
                                color: '#9CA3AF',
                                display: 'flex'
                            }}
                            title="Delete"
                        >
                            <CloseIcon style={{ fontSize: 18 }} />
                        </button>

                        <button
                            onClick={handleStarClick}
                            style={{
                                background: 'none',
                                border: 'none',
                                padding: '4px',
                                cursor: 'pointer',
                                color: isStarred ? '#F59E0B' : '#9CA3AF',
                                display: 'flex'
                            }}
                            title="Star"
                        >
                            {isStarred ? <StarIcon style={{ fontSize: 18 }} /> : <StarBorderIcon style={{ fontSize: 18 }} />}
                        </button>

                        <button
                            ref={buttonRef}
                            onClick={userId ? () => handleBookmarkClick(null) : () => openNewFolderModal && openNewFolderModal(true)}
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                            style={{
                                background: 'none',
                                border: 'none',
                                padding: '4px',
                                cursor: 'pointer',
                                color: bookmarked ? '#2563EB' : '#9CA3AF',
                                display: 'flex'
                            }}
                            title="Bookmark"
                        >
                            {bookmarked ? <BookmarkAddedIcon style={{ fontSize: 18 }} /> : <BookmarkBorderIcon style={{ fontSize: 18 }} />}
                        </button>
                    </div>
                </div>

                {/* Explanation - shown when confidence is clicked */}
                {showExplanation && post.explanation && (
                    <div style={{
                        padding: '10px 12px',
                        backgroundColor: '#F9FAFB',
                        borderRadius: '6px',
                        marginBottom: '12px',
                        borderLeft: '3px solid #D1D5DB'
                    }}>
                        <p style={{
                            margin: 0,
                            fontSize: '12px',
                            color: '#6B7280',
                            fontStyle: 'italic',
                            lineHeight: 1.5
                        }}>
                            <strong style={{ fontStyle: 'normal', color: '#4B5563' }}>Why: </strong>
                            {post.explanation}
                        </p>
                        {(userId === 'ce690a5b-f97d-448c-aa73-b8677f476c86' || userId === '7e532348-5702-4cd2-9524-cae6b8f31ae8') && (
                            <button
                                onClick={handleDeleteRating}
                                style={{
                                    marginTop: '8px',
                                    padding: '4px 8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    background: '#ff4444',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '11px'
                                }}
                            >
                                <DeleteIcon style={{ fontSize: 14 }} />
                                Delete Assessment
                            </button>
                        )}
                    </div>
                )}

                {/* Post text */}
                <div style={{ marginBottom: '12px' }}>
                    {renderText()}
                </div>

                {/* Footer links */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    fontSize: '12px'
                }}>
                    {post.url && (
                        <a
                            href={post.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                color: '#2563EB',
                                textDecoration: 'none'
                            }}
                        >
                            Source <OpenInNewIcon style={{ fontSize: 14 }} />
                        </a>
                    )}
                    <a
                        href={`/post/${post.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            color: '#6B7280',
                            textDecoration: 'none'
                        }}
                    >
                        See Context
                    </a>
                </div>
            </div>

            {/* Bookmark folder menu */}
            {isHovered && userId && (
                <div
                    ref={menuRef}
                    onMouseEnter={handleMenuMouseEnter}
                    onMouseLeave={handleMenuMouseLeave}
                    style={{
                        position: 'fixed',
                        top: menuPosition.y,
                        right: menuPosition.x,
                        backgroundColor: 'white',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        zIndex: 1000,
                        minWidth: '160px',
                        maxHeight: '300px',
                        overflowY: 'auto'
                    }}
                >
                    <ul style={{ listStyle: 'none', margin: 0, padding: '4px 0' }}>
                        {foldersInOrder.map((folder) => (
                            <li key={folder.folder_id}>
                                <button
                                    onClick={() => handleBookmarkClick(folder.folder_id)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        width: '100%',
                                        padding: '8px 12px',
                                        background: bookmarked && bookmarkFolder === folder.folder_id ? '#EFF6FF' : 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        color: bookmarked && bookmarkFolder === folder.folder_id ? '#2563EB' : '#374151',
                                        textAlign: 'left'
                                    }}
                                >
                                    {folder.folder_name}
                                    {bookmarked && bookmarkFolder === folder.folder_id && (
                                        <CheckIcon style={{ fontSize: 16, color: '#2563EB' }} />
                                    )}
                                </button>
                            </li>
                        ))}
                        <li style={{ borderTop: '1px solid #E5E7EB', marginTop: '4px', paddingTop: '4px' }}>
                            <button
                                onClick={handleNewFolder}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    width: '100%',
                                    padding: '8px 12px',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    color: '#6B7280'
                                }}
                            >
                                <AddIcon style={{ fontSize: 16 }} />
                                New Folder
                            </button>
                        </li>
                    </ul>
                </div>
            )}
        </div>
    );
}

export default MotivationPost;
